import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import {
  satakuntaCodes,
  serviceTemplate,
  organizationTemplate,
} from '../constants.js';
import {
  filterValidServicesFromAPIResponse,
  normalizeOrganizationData,
  normalizeServiceChannelData,
  mapPharmaciesToMunicipality,
} from '../utils/helpers.js';

const APIURL =
  'https://api.palvelutietovaranto.suomi.fi/api/v11/';

const WP_APIURL = '/wp-json/rest/v2/';

const addLocationQuery = WP_APIURL + 'add_location_query';
const addOrganizationQuery =
  WP_APIURL + 'add_organization_query';
const addServiceFilterQuery =
  WP_APIURL + 'add_service_filter_query';

// Helper functions
const getMunicipalityServiceUrl = (
  municipalityCode,
  pageNumber,
) =>
  APIURL +
  `Service/list/area/Municipality/code/${municipalityCode}?includeWholeCountry=false&serviceWithGD=false&page=${pageNumber}&showHeader=false`;

const getOrganizationUrl = (organizationCode) =>
  APIURL + `Organization/${organizationCode}`;

const getServicesByOrganizationUrl = (organizationCode) =>
  APIURL +
  `Service/list/organization?organizationId=  ${organizationCode}`;

const getServiceChannelsByIdsUrl = (serviceChannelIds) =>
  APIURL + `ServiceChannel/list?guids=${serviceChannelIds}`;

// Main hook
function useApiData() {
  const [status, setStatus] = useState('initial');

  const [
    selectedMunicipality,
    setSelectedMunicipality,
  ] = useState('');

  const [
    municipalityServices,
    setMunicipalityServices,
  ] = useState(serviceTemplate());

  const [filteredServices, setFilteredServices] = useState(
    [],
  );

  const [organizationData, setOrganizationData] = useState(
    organizationTemplate(),
  );

  const [serviceChannels, setServiceChannels] = useState(
    [],
  );

  const [activeFilters, setActiveFilters] = useState([]);

  const [
    activeExtraFilters,
    setActiveExtraFilters,
  ] = useState([]);

  const municipalities = satakuntaCodes;

  function setValidServicesToTemplate(validServices) {
    // set all found services to correct places in the filtering table
    const template = serviceTemplate();

    for (const validService of validServices) {
      Object.keys(template).forEach((category) => {
        for (const serviceKey of validService.keys) {
          if (
            template[category].hasOwnProperty(serviceKey)
          ) {
            // add additional organization information to each item
            template[category][serviceKey].data.push(
              validService,
            );
          }
        }
      });
    }

    return template;
  }

  /*
    TODO: Don't use hard-coded municipalities if service is scaled nationwide

    const [municipalities, setMunicipalities] = useState([]);
    useEffect(() => {
        setStatus('loading')
        const municipalitiesUrl = APIURL + 'CodeList/GetMunicipalityCodes'
        axios.get(municipalitiesUrl)
            .then(function (response) {
                console.log(response);
                setMunicipalities(response.data)
            }).catch(function (error) {
                console.log(error);
            }).finally(() => {
                setState('ready')
            })
    }, []);
    */

  useEffect(
    function applyFilters() {
      /* filter items from municipalityServices (serviceTemplate) -> filtered services 
      contains only list of relevant services (not the multilevel description of UI layer)*/

      const filterEnglish = (service) =>
        service.languages.includes('en');

      const filterSwedish = (service) =>
        service.languages.includes('sv');

      const filterVoucher = (service) =>
        service.serviceVoucherInUse;

      const filterFreeOfCharge = (service) =>
        service.serviceChargeType === 'FreeOfCharge';

      const serviceInExtraFilter = (service, filter) =>
        filter === 'englanti'
          ? filterEnglish(service)
          : filter === 'ruotsi'
          ? filterSwedish(service)
          : filter === 'palveluseteli'
          ? filterVoucher(service)
          : filter === 'maksuton'
          ? filterFreeOfCharge(service)
          : false;

      function serviceInEveryExtraFilter(service) {
        return activeExtraFilters.every((filter) =>
          serviceInExtraFilter(service, filter),
        );
      }

      function serviceInOntologyFilters(service) {
        return service.keys?.some((key) =>
          activeFilters.includes(key),
        );
      }

      function setServicesVisibility(
        services,
        filterFunctions,
      ) {
        for (const service of services) {
          if (
            filterFunctions.every((func) => func(service))
          ) {
            service.showInOrganizationViews = true;
          } else {
            service.showInOrganizationViews = false;
          }
        }
      }

      const allServices = Object.values(
        municipalityServices,
      )
        .flatMap((item) => Object.values(item))
        .map((item) => item.data)
        .flat()
        .filter(
          // filter duplicates
          (service, index, self) =>
            index ===
            self.findIndex(
              (next) => next.id === service.id,
            ),
        );

      if (
        activeFilters.length === 0 &&
        activeExtraFilters.length === 0
      ) {
        setServicesVisibility(allServices, []);
      } else if (
        activeFilters.length === 0 &&
        activeExtraFilters.length !== 0
      ) {
        setServicesVisibility(allServices, [
          serviceInEveryExtraFilter,
        ]);
      } else if (
        activeFilters.length !== 0 &&
        activeExtraFilters.length === 0
      ) {
        setServicesVisibility(allServices, [
          serviceInOntologyFilters,
        ]);
      } else {
        setServicesVisibility(allServices, [
          serviceInEveryExtraFilter,
          serviceInOntologyFilters,
        ]);
      }

      setFilteredServices(
        allServices.sort((a, b) =>
          (a.name || '').localeCompare(b.name || ''),
        ),
      );
    },
    [
      activeFilters,
      activeExtraFilters,
      municipalityServices,
    ],
  );

  const handleOrganizationData = useCallback(
    async (validServices) => {
      const organizations = {};
      // set available services to organization object
      for (const validService of validServices) {
        const organizationKey =
          validService.organizations[0];

        if (organizations.hasOwnProperty(organizationKey)) {
          organizations[
            organizationKey
          ].availableServices.push(...validService.keys);
        } else {
          organizations[organizationKey] = {
            availableServices: [...validService.keys],
          };
        }
      }

      // fetch data from found organizations
      for (const [
        organizationKey,
        availableServices,
      ] of Object.entries(organizations)) {
        const orgResponse = await axios.get(
          getOrganizationUrl(organizationKey),
        );

        // remove duplicates from available services
        availableServices.availableServices = [
          ...new Set(availableServices.availableServices),
        ];

        organizations[organizationKey] = {
          ...availableServices,
          ...normalizeOrganizationData(orgResponse),
        };
      }

      setOrganizationData({
        ...organizationData,
        ...organizations,
      });

      setStatus('ready');
    },
    [organizationData],
  );

  const getRestOfTheMunicipalityServices = useCallback(
    async (pageCount, municipalityCode, validServices) => {
      try {
        setStatus('loading-extra');
        const requests = [];
        // request to page number 1 is already made in order to find out total page count
        for (
          let pageNumber = 2;
          pageNumber <= pageCount;
          pageNumber++
        ) {
          requests.push(
            axios.get(
              getMunicipalityServiceUrl(
                municipalityCode,
                pageNumber,
              ),
            ),
          );
        }

        const responses = await axios.all(requests);

        responses.flatMap((response) => {
          validServices = [
            ...validServices,
            ...filterValidServicesFromAPIResponse(response),
          ];
        });

        const updateServices = mapPharmaciesToMunicipality(
          setValidServicesToTemplate(validServices),
          municipalityCode,
        );

        setMunicipalityServices(updateServices);

        handleOrganizationData(validServices);
      } catch (err) {
        // TODO: communicate errors to user
        console.log(err);
        setStatus('ready');
      }
    },
    [handleOrganizationData],
  );

  const fetchServicesFromMunicipality = useCallback(
    async (municipalityCode) => {
      setStatus('loading');
      setSelectedMunicipality(municipalityCode);
      setMunicipalityServices(serviceTemplate());

      try {
        // fetch services
        const response = await axios.get(
          getMunicipalityServiceUrl(municipalityCode, 1),
        );

        const validServices = filterValidServicesFromAPIResponse(
          response,
        );

        if (response.data.pageCount > 1) {
          getRestOfTheMunicipalityServices(
            response.data.pageCount,
            municipalityCode,
            validServices,
          );
        } else {
          const updateServices = mapPharmaciesToMunicipality(
            setValidServicesToTemplate(validServices),
            municipalityCode,
          );

          setMunicipalityServices(updateServices);

          handleOrganizationData(validServices);

          setStatus('ready');
        }
      } catch (err) {
        // TODO: communicate errors to user
        console.log(err);
        setStatus('ready');
      }
    },
    [
      getRestOfTheMunicipalityServices,
      handleOrganizationData,
    ],
  );

  const fetchServiceChannels = useCallback(
    async (serviceChannelIds) => {
      if (serviceChannelIds.length === 0) {
        return;
      }

      const respone = await axios.get(
        getServiceChannelsByIdsUrl(
          serviceChannelIds.join(','),
        ),
      );

      const newChannels = respone.data?.map((channel) =>
        normalizeServiceChannelData(channel),
      );

      setServiceChannels((prevServiceChannels) => [
        ...prevServiceChannels,
        ...newChannels,
      ]);
    },
    [],
  );

  const fetchOrganizationData = useCallback(
    async (organizationCode) => {
      if (['loading-extra', 'error'].includes(status)) {
        return;
      }
      // Service ids for fetching service channels
      let serviceIds = null;
      try {
        // Fetch organization details if they aren't in cache
        if (!(organizationCode in organizationData)) {
          setStatus('loading-extra');

          const organizationResponse = await axios.get(
            getOrganizationUrl(organizationCode),
          );

          // TODO: over 100 services in reponse
          const serviceResponse = await axios.get(
            getServicesByOrganizationUrl(organizationCode),
          );

          const validServices = filterValidServicesFromAPIResponse(
            serviceResponse,
          );

          setOrganizationData({
            [organizationCode]: {
              ...normalizeOrganizationData(
                organizationResponse,
              ),
              availableServices: [
                ...new Set(
                  validServices.flatMap(
                    (service) => service.keys,
                  ),
                ),
              ],
            },
          });
          setFilteredServices(
            validServices.sort((a, b) =>
              (a.name || '').localeCompare(b.name || ''),
            ),
          );
          serviceIds = [
            // Get all unique service channel IDs from valid services
            ...new Set(
              validServices
                .map((service) => service.serviceChannels)
                .flat(),
            ),
          ];
        } else {
          serviceIds = filteredServices
            .filter((service) =>
              service.organizations.includes(
                organizationCode,
              ),
            )
            .flatMap((item) => item.serviceChannels);
        }
        // Fetch service channels
        const alreadyFetched = serviceChannels.map(
          (item) => item.id,
        );
        await fetchServiceChannels(
          serviceIds.filter(
            (serviceId) =>
              !alreadyFetched.includes(serviceId),
          ),
        );
        setStatus('ready');
      } catch (err) {
        console.log(err);
        setStatus('error');
      }
    },
    [
      fetchServiceChannels,
      filteredServices,
      organizationData,
      serviceChannels,
      status,
    ],
  );

  function resetFilters() {
    setActiveFilters([]);
  }

  function toggleFilter(term) {
    setActiveFilters(
      activeFilters.includes(term)
        ? activeFilters.filter((item) => item !== term)
        : [...activeFilters, term],
    );

    if (!activeFilters.includes(term)) {
      logServiceFilterChange(term, selectedMunicipality);
    }
  }

  function toggleExtraFilter(term) {
    setActiveExtraFilters(
      activeExtraFilters.includes(term)
        ? activeExtraFilters.filter((item) => item !== term)
        : [...activeExtraFilters, term],
    );

    if (!activeFilters.includes(term)) {
      logServiceFilterChange(term, selectedMunicipality);
    }
  }

  function clearFilters() {
    setActiveFilters([]);
    setActiveExtraFilters([]);
  }

  const logMunicipalityChange = async (locationCode) => {
    const municipality = satakuntaCodes.find(
      (municipality) => municipality.code === locationCode,
    );
    await axios.post(addLocationQuery, {
      location: municipality.names[0].value,
      timestamp: null, // ON UPDATE TIMESTAMP
    });
  };

  const logOrganizationChange = useCallback(
    async (id, organization) => {
      await axios.post(addOrganizationQuery, {
        apiId: id,
        organization: organization,
        timestamp: null, // ON UPDATE TIMESTAMP
      });
    },
    [],
  );

  const logServiceFilterChange = useCallback(
    async (filter, municipalityCode) => {
      await axios.post(addServiceFilterQuery, {
        location: satakuntaCodes.find(
          (municipality) =>
            municipality.code === municipalityCode,
        ).names[0].value,
        filter: filter,
        timestamp: null, // ON UPDATE TIMESTAMP
      });
    },
    [],
  );

  return {
    status,
    selectedMunicipality,
    municipalities,
    fetchServicesFromMunicipality,
    organizationData,
    fetchOrganizationData,
    resetFilters,
    activeFilters,
    toggleFilter,
    municipalityServices,
    filteredServices,
    toggleExtraFilter,
    activeExtraFilters,
    clearFilters,
    fetchServiceChannels,
    serviceChannels,
    logMunicipalityChange,
    logOrganizationChange,
  };
}

export default useApiData;
